<script>
   /****************************************************************
   * StatCompPlot                                                  *
   * --------------------                                          *
   * shows plot with a parameter value vs. number of PCs           *
   *****************************************************************/

   import { Axes, XAxis, YAxis, Box, Segments, Legend } from 'svelte-plots-basic/2d';
   import { PNG_WIDTH, PNG_HEIGHT, PNG_RES, MARGINS, LINE_COLORS, FACE_COLORS } from './Defaults.js';
   import StatCompSeries from './StatCompSeries.svelte';

   // component parameters
   export let params;                        // JSON with main plot parameters
   export let res;                           // JSON with result objects (SIMCA, PCA, etc)
   export let ncomp = 1;                     // number of components selected by user
   export let limYMin = undefined;           // let parent component to force min Y value
   export let limYMax = undefined;           // let parent component to force max Y value

   // plot parameters
   export let statName                       // name of statistics to show (variance, sensitivity, etc.)
   export let showGrid = true;
   export let xLabel;
   export let yLabel;
   export let title;
   export let legendPosition = 'topright';

   // saving parameters
   export let fileName;
   export let pngWidth = PNG_WIDTH;
   export let pngHeight = PNG_HEIGHT;
   export let pngRes = PNG_RES;


   /*****************************************/
   /* Component code                        */
   /*****************************************/

   let yMax = Number.NEGATIVE_INFINITY;
   let yMin = Number.POSITIVE_INFINITY;

   // select which results to display
   $: resID = params && params.resID ? params.resID : 'both';     // selected result to show
   $: resobjIDsAll = res ? Object.keys(res) : null;               // all IDs of results object
   $: resobjIDs = resobjIDsAll ? (resID !== 'both' && resobjIDsAll.includes(resID) ? [resID] : resobjIDsAll) : null;

   // compute max number of components and collect legend items
   $: legendItems = resobjIDs ? resobjIDs.map(v => ({
      id: v,
      label: v,
      marker: 1,
      lineColor: LINE_COLORS[v],
      borderColor: LINE_COLORS[v],
      faceColor: FACE_COLORS[v]
   })) : null;

   // compute limits
   $: dY = (yMax - yMin) * 0.20;
   $: limY = [(limYMin ? limYMin : yMin - dY), (limYMax ? limYMax : yMax + dY)];
   $: limX = resobjIDs ? [0.5, res[resobjIDs[0]].stat.components.length + 0.5] : [0, 1];
</script>

{#if res }
<Axes {title} {xLabel} {yLabel} {limX} {limY}
   {fileName} {pngWidth} {pngHeight} {pngRes}
   downloadLinks="hover"
   margins={MARGINS}
>
   <slot name="before"></slot>

   <!-- vertical line with selected component -->
   <Segments xStart={[ncomp]} xEnd={[ncomp]} yStart={[limY[0]]} yEnd={[limY[1]]} lineColor='#a0a0a0' />

   <!-- data points with statistics -->
   {#each resobjIDs as id}
      <StatCompSeries
         {ncomp} {statName}
         lineColor={LINE_COLORS[id]}
         faceColor={FACE_COLORS[id]}
         resobj={res[id]}
         bind:minVal={yMin}
         bind:maxVal={yMax}
      />
   {/each}

   <slot name="after"></slot>

   {#if legendItems && legendItems.length > 0}
   <Legend items={legendItems} position={legendPosition} />
   {/if}

   <XAxis {showGrid} slot="xaxis" whole={true} />
   <YAxis {showGrid} slot="yaxis" />
   <Box slot="box" />
</Axes>
{/if}